// src/pages/SignUpHybrid.js
import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";
import GoogleButton from "../../components/common/GoogleButton";
import LinkedInButton from "../../components/common/LinkedInButton";

export default function SignUpHybrid() {
  // steps: "email" -> "otp" -> "password"
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [tempAccessToken, setTempAccessToken] = useState(null);
  const [legacyMode, setLegacyMode] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  // ✅ Use env variable like in LoginPage
  const API_BASE = process.env.REACT_APP_API_URL || "https://localhost:7211";
  const API = `${API_BASE}/api/auth`;

  // STEP 1 — Send OTP
  const handleSendOtp = async () => {
    if (!email.trim()) {
      toast({
        title: "Missing email",
        description: "Please enter your email.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({
          title: "Signup failed",
          description: data.message || "Unable to send OTP.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      toast({
        title: "OTP sent",
        description: "Check your email for the OTP.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      setStep("otp");
    } catch (e) {
      toast({
        title: "Network error",
        description: "Could not reach the server.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2 — Validate OTP
  const handleValidateOtp = async () => {
    if (!otp.trim()) {
      toast({
        title: "Missing OTP",
        description: "Please enter the OTP you received.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), token: otp.trim() }),
      });
      const rawText = await res.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }

      if (res.ok) {
        const access =
          data.access_token ||
          data.AccessToken ||
          data.session?.access_token ||
          data.Session?.AccessToken ||
          null;
        if (access) setTempAccessToken(access);

        toast({
          title: "OTP verified",
          description: "Great! Now set your password.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        setStep("password");
        return;
      }

      setLegacyMode(true);
      toast({
        title: "OTP received",
        description: "Now set your password to complete signup.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      setStep("password");
    } catch (e) {
      setLegacyMode(true);
      toast({
        title: "Connection issue",
        description: "Proceed to set your password to finish.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      setStep("password");
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3 — Set Password
  const handleSetPassword = async () => {
    if (!password.trim()) {
      toast({
        title: "Missing password",
        description: "Please set your password.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsLoading(true);
    try {
      if (!legacyMode && tempAccessToken) {
        const res = await fetch(`${API}/set-password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tempAccessToken}`,
          },
          body: JSON.stringify({ password: password.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast({
            title: "Password update failed",
            description: data.message || "Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const access =
          data.access_token ||
          data.AccessToken ||
          data.session?.access_token ||
          data.Session?.AccessToken ||
          null;
        const refresh =
          data.refresh_token ||
          data.RefreshToken ||
          data.session?.refresh_token ||
          data.Session?.RefreshToken ||
          null;

        if (access) localStorage.setItem("token", access);
        if (refresh) localStorage.setItem("refreshToken", refresh);

        if (!access) {
          const sRes = await fetch(`${API}/signin`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: email.trim(),
              password: password.trim(),
            }),
          });
          const sData = await sRes.json().catch(() => ({}));
          if (!sRes.ok || !sData.access_token) {
            toast({
              title: "Signin failed",
              description: "Please log in manually.",
              status: "warning",
              duration: 3000,
              isClosable: true,
            });
            navigate("/login");
            return;
          }
          localStorage.setItem("token", sData.access_token);
          if (sData.refresh_token)
            localStorage.setItem("refreshToken", sData.refresh_token);
        }

        toast({
          title: "Welcome!",
          description: "Your account is ready.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        navigate("/me");
        return;
      }

      const res = await fetch(`${API}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          token: otp.trim(),
          password: password.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({
          title: "Signup failed",
          description: data.message || "Invalid OTP or password.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const access = data.access_token || data.session?.access_token;
      const refresh = data.refresh_token || data.session?.refresh_token;

      if (access) localStorage.setItem("token", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);

      toast({
        title: "Welcome!",
        description: "Your account is ready.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      navigate("/me");
    } catch (e) {
      toast({
        title: "Network error",
        description: "Could not reach the server.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign Up for SkillBack - Your Unfair Advantage in Tech Hiring"
        description="Join SkillBack free today. Build resumes that get you interviews. Made for developers, engineers, and tech professionals. Start landing your dream role."
        keywords="SkillBack signup, join SkillBack, free tech resume builder, developer resume, engineer resume"
        canonical="https://skillback.io/signup"
      />

      <Container display="flex" alignItems="flex-start" justifyContent="center">
      <Box
        mt="0"
        w={{ base: "90%", sm: "340px" }}
        p={{ base: "1.25rem", sm: "2rem" }}
        borderRadius="xl"
        boxShadow="md"
        bg={useColorModeValue("white", "gray.800")}
      >
        <Stack spacing="1.2rem" align="stretch">
          <Heading size="md" textAlign="center" mb="0.5rem">
            {step === "email"
              ? "Sign Up"
              : step === "otp"
              ? "Verify OTP"
              : "Set Password"}
          </Heading>

          {(step === "email" || step === "password") && (
            <FormControl id="email">
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                placeholder="tanaki.haruki@skillback.io"
                size="sm"
                value={email}
                isDisabled={step !== "email"}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
          )}

          {step === "otp" && (
            <FormControl id="otp">
              <FormLabel>OTP</FormLabel>
              <Input
                type="text"
                placeholder="Enter the code"
                size="sm"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </FormControl>
          )}

          {step === "password" && (
            <FormControl id="password">
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                placeholder="Set your password"
                size="sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
          )}

          <Button
            type="button"
            onClick={
              step === "email"
                ? handleSendOtp
                : step === "otp"
                ? handleValidateOtp
                : handleSetPassword
            }
            colorScheme="blue"
            size="sm"
            w="full"
            mt="0.5rem"
            isLoading={isLoading}
          >
            {step === "email"
              ? "Send OTP"
              : step === "otp"
              ? "Verify OTP"
              : "Set Password"}
          </Button>

          {step === "email" && (
            <>
              <Flex align="center" my="0.5rem">
                <Divider />
                <Text px="3" fontSize="xs" color="gray.500" whiteSpace="nowrap">
                  or sign up with
                </Text>
                <Divider />
              </Flex>

              <GoogleButton />
              <LinkedInButton />
            </>
          )}
        </Stack>
      </Box>
    </Container>
    </>
  );
}
